import { Injectable, EventEmitter } from '@angular/core';
import { Router } from '@angular/router';
import {
  User,
  UserManager,
  UserManagerSettings,
  WebStorageStateStore,
} from 'oidc-client';
import {
  from as observableFrom, 
  Observable,
  Subscriber,
  of as observableOf,
  Subscription,
} from 'rxjs';
import { ApiRoutingUrl } from 'src/app/shared/Constants/api-routing-url';
import { UserSessionService } from '../../shared/services/user-session/user-session.service';
import { LoginService } from '../../login/services/login-service/login.service';
import { switchMap } from 'rxjs/operators';
import { AppRoutes } from '../../shared/Constants/app-routes';
import { AuthenticatedResult } from './auth.interface';
import { AppConfigService } from '../../shared/services/app-config/app-config.service';
import { HttpRequestService } from 'src/app/shared/services/http-request/http-request.service';

@Injectable()
export class AuthService {
  private isSessionClosed: boolean = false;
  private _webUrl = window.location.origin;
  public onAuthenticationComplete: EventEmitter<AuthenticatedResult> =
    new EventEmitter();
  public onCMSAuthenticationComplete: EventEmitter<boolean> =
    new EventEmitter();
  public clientSettings: UserManagerSettings = {
    authority: this.appConfig.config.login_url,
    client_id: this.appConfig.config.identityServerClientId,
    redirect_uri: this._webUrl + '/auth-callback',
    post_logout_redirect_uri: this._webUrl + '/index.html',
    response_type: 'id_token token',
    scope: this.appConfig.config.identityServerScopes,
    filterProtocolClaims: true,
    loadUserInfo: true,
    automaticSilentRenew: true,
    silent_redirect_uri: this._webUrl + '/silent-refresh.html',
    monitorSession: true,
    includeIdTokenInSilentRenew: true,
    userStore: new WebStorageStateStore({ store: localStorage }),
    checkSessionInterval: 2000,
  };

  private manager: UserManager = new UserManager(this.clientSettings);
  constructor(
    private _router: Router,
    private _userSessionService: UserSessionService,
    private _loginService: LoginService,
    private appConfig: AppConfigService,
    private http: HttpRequestService
  ) {
    this.listenToLogoutEvent();
    this._webUrl = window.location.origin;
    this.setUserManagerEvents();

    let sessionStateChanged: Subscription = this._userSessionService
      .sessionStateChanged()
      .subscribe(isActive => {
        if (!isActive && this.isSessionClosed == false) {
          this.isSessionClosed = true;

          sessionStateChanged.unsubscribe();
        }
      });
  }

  private setUserManagerEvents(): void {
    this.manager.events.addSilentRenewError(err => {
      console.log('SilentRenewError:' + err);
    });

    this.manager.events.addAccessTokenExpiring(() => {
      console.log('Access token expiring on ' + new Date());
    });

    this.manager.events.addAccessTokenExpired(() => {
      console.log('Access token expired on ' + new Date());
      this._userSessionService.onLogout.emit();
    });

    this.manager.events.addUserSignedOut(() => {
      this.manager.signoutRedirect().then(x => {
        window.location.href = '/';
      });
    });
  }

  public isLoggedIn(): Observable<boolean> {
    return Observable.create((s: Subscriber<boolean>) => {
      this.manager.getUser().then(user => {
        let isAuthenticated = user != null && !user.expired;
        s.next(isAuthenticated);
        s.complete();
      });
    });
  }

  public authenticateWithCMS(): Observable<boolean> {
    return this.isLoggedIn().pipe(
      switchMap(isLogged => {
        if (isLogged) {
          return this.getCurrentUser().pipe(
            switchMap((user: User) => {
              let token = user.access_token;
              let encodedUsername = user.profile.name;

              //get token from cms then redirect to ebook
              return this._loginService
                .authenticateWithIdentityToken(token, encodedUsername)
                .pipe(
                  switchMap(result => {
                    let retVal: Observable<any>;
                    if (result.success) {
                      if (!result.user.hasConsentedAgreement) {
                        retVal = observableOf(
                          this._router.navigateByUrl(AppRoutes.userAgreement, {
                            state: { data: result },
                          })
                        );
                      } else {
                        this._userSessionService.startUserSession(
                          result.user.access_token,
                          result.user,
                          null
                        );
                        retVal = observableOf(
                          this._router.navigateByUrl(AppRoutes.ebook)
                        );
                      }
                    } else {
                      this._router.navigate([AppRoutes.loginErrorPage], {
                        state: { data: result.error },
                      });
                      return observableOf(false);
                    }
                    this.onCMSAuthenticationComplete.emit(true);
                    return retVal;
                  })
                );
            })
          );
        } else {
          this.startAuthentication();
          return observableOf(false);
        }
      })
    );
  }

  public startAuthentication(): Promise<void> {
    return this.manager.signinRedirect().catch(e => {});
  }

  public listenToLogoutEvent() {
    this._userSessionService.onLogout.subscribe(response => {
      this._userSessionService.handleSessionClose();
      this._userSessionService.endUserSession();

      if (this.appConfig.config.useSDPIdentityServerAuthentication) {
        localStorage.removeItem('idle');
        sessionStorage.removeItem('idle');
        this.manager.clearStaleState();
        this.manager.signoutRedirect();
      }
    });
  }

  public completeAuthentication(): Observable<User> {
    return Observable.create((s: Subscriber<User>) => {
      this.manager
        .signinRedirectCallback()
        .then((user: User) => {
          let isAuthenticated = user != null && !user.expired;
          this.onAuthenticationComplete.emit({
            isAuthenticated: isAuthenticated,
            user: user,
          });

          if (isAuthenticated) {
            s.next(user);
            s.complete();
          } else {
            this._router.navigate(['/']);
          }
        })
        .catch(err => {
          this._router.navigateByUrl('/');
        });
    });
  }

  public getCurrentUser(): Observable<User> {
    return observableFrom(this.manager.getUser());
  }

  public getPrivacyPolicyContent(): Observable<string> {
    let url: string = ApiRoutingUrl.usersGDPRConsentContent;
    return this.http.get(url);
  }
}
